import { QueryClient, QueryFunction } from "@tanstack/react-query";

/** Errore HTTP con lo status, così le pagine distinguono un 404 da un guasto di rete o del server. */
export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = "ApiError";
  }
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const contentType = res.headers.get("content-type") || "";
    let text = (await res.text()) || res.statusText;

    const looksLikeHtml =
      contentType.includes("text/html") ||
      text.toLowerCase().includes("<!doctype") ||
      text.toLowerCase().includes("<html");

    if (looksLikeHtml) {
      if (res.status === 502 || res.status === 503 || res.status === 504) {
        text = "Servizio temporaneamente non disponibile. Riprova più tardi.";
      } else {
        text = res.statusText || "Errore del server.";
      }
    } else if (contentType.includes("application/json") && text) {
      try {
        const json = JSON.parse(text);
        const rawMessage = json?.error ?? json?.message;
        if (typeof rawMessage === "string" && rawMessage) {
          let errMsg = rawMessage;
          if (json?.debug && typeof json.debug === "object") {
            const d = json.debug as Record<string, unknown>;
            if (d.raw || d.code || d.detail) {
              errMsg += ` [${[d.code, d.detail, d.raw].filter(Boolean).join(" | ")}]`;
            }
          }
          text = errMsg;
        }
      } catch (_) {}
    }

    throw new ApiError(text, res.status);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  if (import.meta.env.DEV) {
    console.log(`API request: ${method} ${url}`, data);
  }
  
  const headers: Record<string, string> = {};
  if (data) headers["Content-Type"] = "application/json";
  // Cache-busting solo per mutazioni, non per GET
  if (method !== "GET") {
    headers["Cache-Control"] = "no-cache";
    headers["Pragma"] = "no-cache";
  }

  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
    ...(method !== "GET" ? { cache: "no-store" as RequestCache } : {}),
  });

  if (import.meta.env.DEV && res.status !== 401) {
    console.log(`API response status: ${res.status}`, url);
  }
  
  try {
    await throwIfResNotOk(res);
    if (import.meta.env.DEV) {
      console.log(`API success: ${method} ${url}`);
    }
    return res;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error(`API error: ${method} ${url}`, error);
    }
    throw error;
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const baseUrl = queryKey[0] as string;
    let url = baseUrl;
    
    // Se abbiamo parametri di ricerca in queryKey[1], costruisci l'URL con query params
    if (queryKey.length > 1 && typeof queryKey[1] === 'object' && queryKey[1] !== null) {
      const params = new URLSearchParams();
      const searchParams = queryKey[1] as Record<string, any>;
      
      // Aggiungi solo i parametri che hanno un valore
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value) && value.length > 0) {
            // Gestisci array (come tags)
            params.set(key, value.join(','));
          } else if (key === 'yearRange' && Array.isArray(value) && value.length === 2) {
            // Gestisci intervallo anni
            params.set(key, value.join(','));
          } else if (typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean') {
            // Gestisci valori semplici
            params.set(key, String(value));
          }
        }
      });
      
      // Aggiungi i parametri all'URL se ce ne sono
      const queryString = params.toString();
      if (queryString) {
        url = `${baseUrl}?${queryString}`;
      }
    }
    
    // Solo log per debugging in development
    if (import.meta.env.DEV) {
      console.log(`Query fetch: ${url}`);
    }
    
    const res = await fetch(url, {
      credentials: "include",
    });

    // Riduci logging per evitare interferenze con GTM
    if (import.meta.env.DEV && res.status !== 401) {
      console.log(`Query response: ${url} status: ${res.status}`);
    }
    
    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      // Non loggare errori 401 per evitare rumore nella console
      return null;
    }

    try {
      await throwIfResNotOk(res);
      const data = await res.json();
      if (import.meta.env.DEV) {
        console.log(`Query success: ${url}`, data);
      }
      return data;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error(`Query error: ${url}`, error);
      }
      throw error;
    }
  };

// Funzione di utilità per invalidare e riaggiornare una query
export const invalidateAndRefetch = async (queryKey: unknown[]) => {
  console.log(`Invalidating and refetching query: ${Array.isArray(queryKey) ? queryKey[0] : queryKey}`);
  
  // Questa è la versione semplificata che dovrebbe funzionare meglio
  // Prima si fa un'invalidazione che marca i dati come stale
  await queryClient.invalidateQueries({queryKey});
  
  // Poi forziamo un refetch che sarà immediatamente visibile all'utente 
  return queryClient.refetchQueries({queryKey});
};

// Query options specifiche per dati statici (cache più lunga)
const staticDataQueryOptions = {
  staleTime: 30 * 60 * 1000, // 30 minuti per dati che cambiano raramente
  gcTime: 60 * 60 * 1000, // 1 ora in cache
};

// Query options per dati dinamici (cache normale)
const dynamicDataQueryOptions = {
  staleTime: 5 * 60 * 1000, // 5 minuti per dati che cambiano spesso
  gcTime: 15 * 60 * 1000, // 15 minuti in cache
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false, // Disabilita il refetch automatico su focus
      refetchOnReconnect: false, // Disabilita il refetch alla riconnessione
      refetchOnMount: true, // Abilitato per evitare dati stale dopo navigazione
      staleTime: 5 * 60 * 1000, // Ridotto a 5 minuti per dati più fresh
      gcTime: 15 * 60 * 1000, // Ridotto per liberare cache più spesso
      retry: false, // Disabilita i retry per evitare chiamate multiple
      retryOnMount: false, // Non ritentare al mount se la query è già fallita
    },
    mutations: {
      retry: 1,
      onSuccess: () => {
        // Invalida cache dopo mutations per evitare inconsistenze
        queryClient.invalidateQueries({ queryKey: ['/api/entities'] });
      }
    },
  },
});
